import type {
  ContributionGrade,
  ContributionGradeState,
} from "@/redux/certifications/types";
import type { ModerationState } from "@/redux/discussions/types";
import type {
  CommunityActivityItem,
  ContributionActivity,
  ContributionStatus,
} from "@/domains/certifications/types";
import {
  CONTRIBUTION_GRADE_STATES,
  CONTRIBUTION_KINDS,
  CONTRIBUTION_STATUSES,
} from "@/domains/certifications/constants";
import {
  MODERATION_STATES,
  UNDER_REVIEW_STATES,
} from "@/domains/moderation/constants";

export const getContributionStatus = (
  moderationState: ModerationState,
  gradeState: ContributionGradeState | null,
): ContributionStatus => {
  if (moderationState === MODERATION_STATES.REJECTED) {
    return CONTRIBUTION_STATUSES.REMOVED;
  }
  if (UNDER_REVIEW_STATES.includes(moderationState)) {
    return CONTRIBUTION_STATUSES.UNDER_REVIEW;
  }
  if (gradeState === CONTRIBUTION_GRADE_STATES.ACCEPTED) {
    return CONTRIBUTION_STATUSES.COUNTS;
  }
  return gradeState === CONTRIBUTION_GRADE_STATES.REJECTED
    ? CONTRIBUTION_STATUSES.DOES_NOT_COUNT
    : CONTRIBUTION_STATUSES.COUNTING_PENDING;
};

export const needsEdit = (status: ContributionStatus) =>
  status === CONTRIBUTION_STATUSES.REMOVED ||
  status === CONTRIBUTION_STATUSES.DOES_NOT_COUNT;

const gradeKey = (kind: CommunityActivityItem["kind"], id: number) =>
  `${kind}-${id}`;

export const withContributionStatus = (
  items: CommunityActivityItem[],
  grades: ContributionGrade[],
): ContributionActivity[] => {
  const gradeByItem = new Map(
    grades.map((grade) => [
      grade.discussion !== null
        ? gradeKey(CONTRIBUTION_KINDS.POST, grade.discussion)
        : gradeKey(CONTRIBUTION_KINDS.COMMENT, grade.comment ?? 0),
      grade,
    ]),
  );
  return items.map((item) => {
    const grade = gradeByItem.get(gradeKey(item.kind, item.id)) ?? null;
    const status = getContributionStatus(
      item.moderationState,
      grade?.state ?? null,
    );
    return {
      ...item,
      status,
      reviewerFeedback:
        status === CONTRIBUTION_STATUSES.DOES_NOT_COUNT
          ? (grade?.feedback ?? "")
          : "",
    };
  });
};
